/**
 * A person on a phone and an iPad, in the Season design: who they are and where they stand, then their story, how far
 * they watched, tags, automations and your notes. A phone docks "Send email"; an iPad holds it in the top card. On the
 * iPad on its side this is the pane beside the list. It does what the computer's page does, from the same hooks.
 */
import { Fragment, useState } from "react";
import type { JourneyItem, PeopleListResponse, PersonDetailResponse } from "../../../shared/types";
import { DockedBar } from "../../components/season/DockedBar";
import { Fold } from "../../components/season/Fold";
import type { SeasonLayout } from "../../components/season/layout";
import { Page } from "../../components/season/Page";
import { PushedNav } from "../../components/season/PushedNav";
import { SeasonCard } from "../../components/season/Card";
import { Button } from "../../components/ui/Button";
import { Ring } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Menu } from "../../components/ui/Menu";
import { Pill } from "../../components/ui/Pill";
import { lastLoaded } from "../../lib/kept";
import { clock, dayMonth, firstName } from "../../lib/when";
import { depthOf } from "./list";
import { byDay, dotOf, PIN_WHAT, systemTagsOf, useAddNote, useAddTag, usePerson, usePersonMenu } from "./person";
import { AddNoteSheet, AddTagSheet } from "./seasonSheets";
import { AutomationSheet, DeleteSheet, NoteSheet } from "./sheets";
import { StagePill } from "./stage";
import "../../styles/season-people.css";

/** The lines of the story a phone shows before "All of it". */
const PHONE_STORY = 3;

export function SeasonPerson({ id, layout, embedded = false }: { id: string; layout: SeasonLayout; embedded?: boolean }) {
  const { d, failed, reload, openThread } = usePerson(id);
  const [emailing, setEmailing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [noting, setNoting] = useState(false);
  const [allStory, setAllStory] = useState(false);
  const addTag = useAddTag(id, reload);
  const addNote = useAddNote(id, reload);
  const menu = usePersonMenu(d, { automation: () => setAdding(true), remove: () => setDeleting(true) });
  const phone = layout === "phone";

  if (!d) {
    return (
      <Page layout={layout} className="pp pp-person">
        {embedded ? null : <PushedNav back={{ to: "/people", label: "People" }} />}
        {failed ? (
          <SeasonCard className="pp-state">
            <span>This person didn't load.</span>
            <Button onClick={reload}>Try again</Button>
          </SeasonCard>
        ) : (
          <p className="k-sub" aria-busy="true">
            Loading
          </p>
        )}
      </Page>
    );
  }

  const p = d.registrant;
  const menuButton = (extra?: string) => <Menu label={`More for ${p.name}`} buttonClassName={extra ? `k-ib ${extra}` : "k-ib"} items={menu} />;
  const kept = lastLoaded<PeopleListResponse>("people:")?.rail.tags ?? [];
  const mine = new Set([...d.own_tags, ...systemTagsOf(d)].map((t) => t.toLowerCase()));
  const suggest = kept.map((t) => t.tag).filter((t) => !mine.has(t.toLowerCase()));

  const story = (
    <SeasonCard className="pp-story">
      <h2 className="k-card-h">
        <span>Their story</span>
        {p.watching ? (
          <Pill tone="good" watching>
            Watching now
          </Pill>
        ) : null}
      </h2>
      {d.journey.length === 0 ? <p className="k-rm">Nothing yet.</p> : null}
      <Events journey={d.journey} limit={phone && !allStory ? PHONE_STORY : undefined} onAnswer={openThread} />
      {phone && !allStory && d.journey.length > PHONE_STORY ? (
        <Fold title="All of it" line={`${d.journey.length - PHONE_STORY} more`} isOpen={false} onClick={() => setAllStory(true)} />
      ) : null}
    </SeasonCard>
  );

  const cards = (
    <>
      {d.pins.map((pin) => (
        <div key={pin.id} className="pp-call">
          <Icon name="pin" />
          <div>
            <b>{`Pinned on their page${pin.until_at ? ` until ${dayMonth(pin.until_at)}` : ""}`}</b>
            <br />
            {pin.title ?? PIN_WHAT[pin.target_kind]}
          </div>
        </div>
      ))}
      {d.answers.length ? (
        <SeasonCard>
          <h2 className="k-card-h">Their answers</h2>
          <ul className="pp-wds">
            {d.answers.map((a) => (
              <li key={a.label}>
                <div className="pp-wd pp-av">
                  <span>{a.label}</span>
                  <b>{a.value}</b>
                </div>
              </li>
            ))}
          </ul>
        </SeasonCard>
      ) : null}
      <SeasonCard>
        <h2 className="k-card-h">Watch depth</h2>
        <ul className="pp-wds">
          {d.webinars.map((g) => (
            <li key={g.registration_id}>
              <div className="pp-wd">
                <span>{g.webinar}</span>
                <b>{`${g.watched_pct}%`}</b>
              </div>
              <i className={g.watched_pct >= 100 ? "pp-bar good" : "pp-bar"}>
                <u style={{ width: `${g.watched_pct}%` }} />
              </i>
            </li>
          ))}
        </ul>
      </SeasonCard>
      <SeasonCard>
        <h2 className="k-card-h">
          <span>Tags</span>
          <button type="button" className="k-link" onClick={() => setTagging(true)}>
            <Icon name="plus" /> Add
          </button>
        </h2>
        <div className="pp-tags">
          {d.own_tags.map((t) => (
            <span key={t} className="tag">
              {t}
            </span>
          ))}
          {systemTagsOf(d).map((t) => (
            <span key={t} className="tag tsys">
              {t}
            </span>
          ))}
        </div>
      </SeasonCard>
      <SeasonCard>
        <h2 className="k-card-h">
          <span>Automations</span>
          <button type="button" className="k-link" onClick={() => setAdding(true)}>
            <Icon name="plus" /> Add
          </button>
        </h2>
        {d.automations.length ? (
          <ul className="pp-autos">
            {d.automations.map((a, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: one person can run through one automation twice
              <li key={`${a.name}-${i}`}>
                <span className="k-ico wine">
                  <Icon name="zap" />
                </span>
                <span>
                  <b>{a.name}</b>
                  <span>{a.sub}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="k-rm">Not in any yet.</p>
        )}
      </SeasonCard>
      <SeasonCard>
        <h2 className="k-card-h">
          <span>Notes</span>
          <button type="button" className="k-link" onClick={() => setNoting(true)}>
            <Icon name="plus" /> Add a note
          </button>
        </h2>
        {d.notes.length ? (
          d.notes.map((n) => (
            <div key={n.id} className="pp-note">
              {n.body}
              <small>{`${firstName(n.author_name)}, ${dayMonth(n.at)}`}</small>
            </div>
          ))
        ) : (
          <p className="k-rm">No notes yet.</p>
        )}
      </SeasonCard>
    </>
  );

  const facts = [p.email, d.phone, d.came_from ? `Came from ${d.came_from}` : ""].filter(Boolean);
  const hero = (
    <section className="pp-hero">
      <div className="pp-hh">
        <Ring name={p.name} photo={p.avatar_url} size={phone ? 76 : 88} percent={depthOf(p)} live={!!p.watching} brass />
        <div className="pp-hn">
          <h1 className="k-h1"><bdi>{p.name}</bdi></h1>
          <div className="pp-pills">
            <StagePill p={p} header />
          </div>
        </div>
      </div>
      <p className="pp-meta">
        {facts.map((f) => (
          <span key={f}>{f}</span>
        ))}
      </p>
      <div className="pp-btns">
        {phone ? null : (
          <Button variant="p" icon="mail" onClick={() => setEmailing(true)}>
            Send email
          </Button>
        )}
        <Button icon="chat" onClick={openThread}>
          View conversation
        </Button>
        {embedded ? menuButton("pp-more-b") : null}
      </div>
    </section>
  );

  return (
    <Page layout={layout} className={phone ? "pp pp-person" : "pp pp-person pp-wide"}>
      {embedded ? null : <PushedNav back={{ to: "/people", label: "People" }} right={menuButton()} />}
      {hero}
      {phone ? (
        <>
          {story}
          {cards}
        </>
      ) : (
        <div className="k-two pp-two">
          <div>{story}</div>
          <div className="pp-col">{cards}</div>
        </div>
      )}
      {phone && !embedded ? (
        <DockedBar>
          <Button variant="p" full icon="mail" onClick={() => setEmailing(true)}>
            Send email
          </Button>
        </DockedBar>
      ) : null}
      <PersonSheets
        d={d}
        emailing={emailing}
        adding={adding}
        deleting={deleting}
        tagging={tagging}
        noting={noting}
        suggest={suggest}
        close={{
          email: () => setEmailing(false),
          automation: () => setAdding(false),
          remove: () => setDeleting(false),
          tag: () => setTagging(false),
          note: () => setNoting(false),
        }}
        onAddTag={addTag}
        onAddNote={addNote}
        reload={reload}
      />
    </Page>
  );
}

function PersonSheets({
  d,
  emailing,
  adding,
  deleting,
  tagging,
  noting,
  suggest,
  close,
  onAddTag,
  onAddNote,
  reload,
}: {
  d: PersonDetailResponse;
  emailing: boolean;
  adding: boolean;
  deleting: boolean;
  tagging: boolean;
  noting: boolean;
  suggest: readonly string[];
  close: Record<"email" | "automation" | "remove" | "tag" | "note", () => void>;
  onAddTag: (tag: string) => Promise<boolean>;
  onAddNote: (text: string) => Promise<boolean>;
  reload: () => void;
}) {
  const p = d.registrant;
  const first = firstName(p.name);
  return (
    <>
      <NoteSheet
        person={emailing ? { id: p.id, name: p.name, avatar_url: p.avatar_url, email: p.email } : null}
        pill={<StagePill p={p} header />}
        onClose={close.email}
        onSent={reload}
      />
      <AutomationSheet open={adding} ids={[p.id]} onClose={close.automation} onAdded={reload} />
      <DeleteSheet open={deleting} id={p.id} name={p.name} onClose={close.remove} />
      <AddTagSheet open={tagging} first={first} suggest={suggest} onAdd={onAddTag} onClose={close.tag} />
      <AddNoteSheet open={noting} first={first} onSave={onAddNote} onClose={close.note} />
    </>
  );
}

// --- the story ---

/** Their story newest first, a day heading over each day; `limit` keeps only the newest few. */
function Events({ journey, limit, onAnswer }: { journey: readonly JourneyItem[]; limit?: number; onAnswer: () => void }) {
  let left = limit ?? Number.POSITIVE_INFINITY;
  return (
    <ol className="pp-evs">
      {byDay(journey).map(([day, items]) => {
        const some = items.slice(0, Math.max(0, left));
        left -= some.length;
        if (some.length === 0) return null;
        return (
          <Fragment key={day}>
            <li className="pp-day">{day}</li>
            {some.map((e, i) => (
              <Event key={`${e.at}-${e.type}-${i}`} e={e} onAnswer={onAnswer} />
            ))}
          </Fragment>
        );
      })}
    </ol>
  );
}

function Event({ e, onAnswer }: { e: JourneyItem; onAnswer: () => void }) {
  const dot = dotOf(e);
  return (
    <li className="pp-ev">
      <span className={dot.tone ? `pp-dot ${dot.tone}` : "pp-dot"}>
        <Icon name={dot.icon} />
      </span>
      <div className="pp-evb">
        <div className="pp-evh">
          <b>{e.title}</b>
          <time>{clock(e.at)}</time>
        </div>
        {e.detail ? <p>{e.detail}</p> : null}
        {e.pct !== undefined ? (
          <i className={e.pct >= 100 ? "pp-bar good" : "pp-bar"}>
            <u style={{ width: `${e.pct}%` }} />
          </i>
        ) : null}
        {e.quote ? <blockquote className="pp-q">{e.quote}</blockquote> : null}
        {e.waiting && e.conversation_id ? (
          <Button className="pp-ans" icon="chat" onClick={onAnswer}>
            Answer
          </Button>
        ) : null}
      </div>
    </li>
  );
}
